import { useEffect, useMemo, useState } from "react";
import { Container, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const LS_KEY = "containerEstimateRules";

export const calcCbmPerPole = (butt, length) => {
  const b = Number(butt), L = Number(length);
  if (!b || !L) return 0;
  return (((b - (L / 2) * 7) / 2) ** 2 * 3.14159 * L) / 1000000;
};

// Physical bundle sizes from ALLOCATED stock (bundle_number groups) — the order
// line's poles_per_bundle can disagree with how the yard actually bundled the poles.
export const stockBundleMap = (allocStock) => {
  const per = {};
  (allocStock || []).forEach((s) => {
    if (s.status !== "allocated") return;
    const k = `${Number(s.length)}|${Number(s.butt_size)}`;
    const bn = s.bundle_number || `__${s.id}`;
    (per[k] = per[k] || {})[bn] = (per[k][bn] || 0) + (Number(s.quantity) || 0);
  });
  const out = {};
  Object.entries(per).forEach(([k, bundles]) => {
    const counts = {};
    Object.values(bundles).forEach((q) => { if (q > 1) counts[q] = (counts[q] || 0) + 1; });
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1] || Number(b[0]) - Number(a[0]))[0];
    if (best) out[k] = Number(best[0]);
  });
  return out;
};

export const withStockBundles = (line, map) => {
  const sb = map[`${Number(line.length)}|${Number(line.butt_size)}`];
  return sb > 1 ? { ...line, bundled: true, poles_per_bundle: sb } : line;
};

const DEFAULTS = { maxCbm: "28", maxWt: "26000", density: "900", maxLengths: "", maxDiams: "",
                   insideL: "12", insideW: "2.35", insideH: "2.69", eff: "95", booked: "", bestFit: "" };

function loadRules(key) {
  const fromStore = (k) => {
    try {
      const raw = localStorage.getItem(k);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const own = fromStore(key);
  if (own) return Object.fromEntries(Object.entries(DEFAULTS).map(([k, v]) => [k, own[k] ?? v]));
  // First time this order is opened: freeze a copy of the current global rules so
  // later tweaks on other orders never change this order's estimate.
  const seedSrc = key !== LS_KEY ? fromStore(LS_KEY) : null;
  const seed = Object.fromEntries(Object.entries(DEFAULTS).map(([k, v]) => [k, k === "booked" ? "" : (seedSrc?.[k] ?? v)]));
  if (key !== LS_KEY) {
    try { localStorage.setItem(key, JSON.stringify(seed)); } catch { /* ignore */ }
  }
  return seed;
}

function pack(groups, rules) {
  const maxCbm = Number(rules.maxCbm) > 0 ? Number(rules.maxCbm) : Infinity;
  const maxWt = Number(rules.maxWt) > 0 ? Number(rules.maxWt) : Infinity;
  const maxL = Number(rules.maxLengths) > 0 ? Math.floor(Number(rules.maxLengths)) : Infinity;
  const maxD = Number(rules.maxDiams) > 0 ? Math.floor(Number(rules.maxDiams)) : Infinity;
  const insideL = Number(rules.insideL) > 0 ? Number(rules.insideL) : Infinity;
  const W = Number(rules.insideW) > 0 ? Number(rules.insideW) * 1000 : 0;
  const H = Number(rules.insideH) > 0 ? Number(rules.insideH) * 1000 : 0;
  const eff = Math.min(100, Number(rules.eff) > 0 ? Number(rules.eff) : 95);
  const cap = (d) => (W && H && d ? Math.max(1, Math.floor(Math.floor(W / d) * Math.floor(H / d) * eff / 100)) : Infinity);
  // Bundled poles never split: quantities per container stay in whole bundles
  // (a final partial bundle may move as one piece).
  const bundleFit = (g, take) => {
    const bund = g.bund > 1 ? g.bund : 1;
    if (bund <= 1 || take >= g.left) return take;
    return Math.floor(take / bund) * bund;
  };


  const rem = groups.map((g) => ({ ...g, left: g.qty }))
    .sort((a, b) => (rules.bestFit ? 0 : (a.pri || 0) - (b.pri || 0)) || b.length - a.length || b.butt - a.butt
      || (b.alloc === true ? 1 : 0) - (a.alloc === true ? 1 : 0));
  const containers = [];
  let guard = 0;
  while (rem.some((g) => g.left > 0) && guard++ < 500) {
    const c = { lengths: new Set(), diams: new Set(), cbm: 0, wt: 0, floor: 0, sections: [] };
    let progress = true;
    while (progress) {
      progress = false;
      // Candidate section length: among the highest-priority groups that fit the
      // remaining floor, pick the length with the BEST own-section fill (a tiny
      // remainder must not anchor a long section and waste its cross-section).
      const eligible = rem.filter((g) => g.left > 0
        && g.length <= insideL - c.floor + 1e-9
        && (c.lengths.has(g.length) || c.lengths.size < maxL));
      if (!eligible.length) break;
      const topPri = eligible[0].pri || 0;
      let secLen = null, bestFill = -1;
      const candGroups = rules.bestFit ? eligible : eligible.filter((g) => (g.pri || 0) === topPri);
      for (const L of new Set(candGroups.map((g) => g.length))) {
        const fill = Math.min(1, rem.filter((g) => g.left > 0 && g.length === L)
          .reduce((a, g) => {
            const cd = cap(g.butt);
            return a + (Number.isFinite(cd) ? g.left / cd : 1);
          }, 0));
        if (fill > bestFill + 1e-9 || (Math.abs(fill - bestFill) <= 1e-9 && L > secLen)) {
          bestFill = fill;
          secLen = L;
        }
      }
      const sec = { length: secLen, contents: [], frac: 0 };
      for (const g of rem) {
        if (g.left <= 0 || g.length !== secLen) continue;
        if (!c.diams.has(g.butt) && c.diams.size >= maxD) continue;
        const capD = cap(g.butt);
        const fitSec = Number.isFinite(capD) ? Math.floor((1 - sec.frac) * capD + 1e-9) : g.left;
        const fitCbm = g.cbmPP > 0 ? Math.floor((maxCbm - c.cbm) / g.cbmPP + 1e-9) : g.left;
        const fitWt = g.wtPP > 0 ? Math.floor((maxWt - c.wt) / g.wtPP + 1e-9) : g.left;
        const take = bundleFit(g, Math.min(g.left, Math.max(0, Math.min(fitSec, fitCbm, fitWt))));
        if (take <= 0) continue;
        sec.contents.push({ length: g.length, butt: g.butt, qty: take, tag: g.tag, alloc: g.alloc, bund: g.bund });
        sec.frac += Number.isFinite(capD) ? take / capD : 0;
        c.lengths.add(g.length);
        c.diams.add(g.butt);
        c.cbm += take * g.cbmPP;
        c.wt += take * g.wtPP;
        g.left -= take;
      }
      if (sec.contents.length === 0) break;
      c.floor += secLen;
      c.sections.push(sec);
      progress = true;
    }
    if (c.sections.length === 0) return { containers, stuck: true };
    for (const sec of c.sections) {
      for (const g of rem) {
        if (g.left <= 0 || g.length >= sec.length) continue;
        if (!c.lengths.has(g.length) && c.lengths.size >= maxL) continue;
        if (!c.diams.has(g.butt) && c.diams.size >= maxD) continue;
        const capD = cap(g.butt);
        const fitSec = Number.isFinite(capD) ? Math.floor((1 - sec.frac) * capD + 1e-9) : g.left;
        const fitCbm = g.cbmPP > 0 ? Math.floor((maxCbm - c.cbm) / g.cbmPP + 1e-9) : g.left;
        const fitWt = g.wtPP > 0 ? Math.floor((maxWt - c.wt) / g.wtPP + 1e-9) : g.left;
        const take = bundleFit(g, Math.min(g.left, Math.max(0, Math.min(fitSec, fitCbm, fitWt))));
        if (take <= 0) continue;
        sec.contents.push({ length: g.length, butt: g.butt, qty: take, tag: g.tag, alloc: g.alloc, bund: g.bund });
        sec.frac += Number.isFinite(capD) ? take / capD : 0;
        c.lengths.add(g.length);
        c.diams.add(g.butt);
        c.cbm += take * g.cbmPP;
        c.wt += take * g.wtPP;
        g.left -= take;
      }
    }
    // Extension pass: a section with spare cross-section space grows into the free
    // floor so longer poles can use it (fills space that would otherwise be wasted).
    for (const sec of c.sections) {
      for (const g of rem) {
        const free = insideL - c.floor;
        if (g.left <= 0 || g.length <= sec.length || g.length > sec.length + free + 1e-9) continue;
        if (!c.lengths.has(g.length) && c.lengths.size >= maxL) continue;
        if (!c.diams.has(g.butt) && c.diams.size >= maxD) continue;
        const capD = cap(g.butt);
        const fitSec = Number.isFinite(capD) ? Math.floor((1 - sec.frac) * capD + 1e-9) : g.left;
        const fitCbm = g.cbmPP > 0 ? Math.floor((maxCbm - c.cbm) / g.cbmPP + 1e-9) : g.left;
        const fitWt = g.wtPP > 0 ? Math.floor((maxWt - c.wt) / g.wtPP + 1e-9) : g.left;
        const take = bundleFit(g, Math.min(g.left, Math.max(0, Math.min(fitSec, fitCbm, fitWt))));
        if (take <= 0) continue;
        sec.contents.push({ length: g.length, butt: g.butt, qty: take, tag: g.tag, alloc: g.alloc, bund: g.bund });
        sec.frac += Number.isFinite(capD) ? take / capD : 0;
        c.lengths.add(g.length);
        c.diams.add(g.butt);
        c.cbm += take * g.cbmPP;
        c.wt += take * g.wtPP;
        c.floor += g.length - sec.length;
        sec.length = g.length;
        g.left -= take;
      }
    }
    // Auto-balance: level the stacks (moving shorter poles between sections) so
    // leftover long poles get the maximum possible top-load headroom.
    if (rem.some((g) => g.left > 0) && c.sections.length > 1) {
      for (let iter = 0; iter < 40; iter++) {
        let hi = null, lo = null;
        for (const s of c.sections) {
          if (!hi || s.frac > hi.frac) hi = s;
          if (!lo || s.frac < lo.frac) lo = s;
        }
        if (!hi || hi === lo || hi.frac - lo.frac < 0.02) break;
        let moved = false;
        for (const x of hi.contents) {
          if (x.span || x.length > lo.length + 1e-9) continue;
          const capD = cap(x.butt);
          if (!Number.isFinite(capD)) continue;
          let k = Math.round(((hi.frac - lo.frac) / 2) * capD);
          if (x.bund > 1) k = Math.floor(k / x.bund) * x.bund;
          k = Math.min(k, x.qty);
          if (k <= 0) continue;
          x.qty -= k;
          hi.frac -= k / capD;
          const ex = lo.contents.find((y) => !y.span && y.tag === x.tag && y.length === x.length && y.butt === x.butt && y.alloc === x.alloc);
          if (ex) ex.qty += k;
          else lo.contents.push({ length: x.length, butt: x.butt, qty: k, tag: x.tag, alloc: x.alloc, bund: x.bund });
          lo.frac += k / capD;
          moved = true;
          break;
        }
        if (!moved) break;
        hi.contents = hi.contents.filter((x) => x.qty > 0);
      }
    }
    // Top-load pass: leftover poles longer than any free section may lie across
    // adjacent sections (limited by the headroom of the fullest stack they cross).
    for (const g of rem) {
      for (let i = 0; i < c.sections.length && g.left > 0; i++) {
        let span = 0, minSpare = 1;
        for (let j = i; j < c.sections.length; j++) {
          span += c.sections[j].length;
          minSpare = Math.min(minSpare, 1 - c.sections[j].frac);
          const tail = j === c.sections.length - 1 ? Math.max(0, insideL - c.floor) : 0;
          if (span + tail + 1e-9 < g.length) continue;
          if (j === i) break; // fits one section — the normal fill/top-up passes own this
          if (!c.lengths.has(g.length) && c.lengths.size >= maxL) break;
          if (!c.diams.has(g.butt) && c.diams.size >= maxD) break;
          const capD = cap(g.butt);
          const fitSec = Number.isFinite(capD) ? Math.floor(minSpare * capD + 1e-9) : g.left;
          const fitCbm = g.cbmPP > 0 ? Math.floor((maxCbm - c.cbm) / g.cbmPP + 1e-9) : g.left;
          const fitWt = g.wtPP > 0 ? Math.floor((maxWt - c.wt) / g.wtPP + 1e-9) : g.left;
          const take = bundleFit(g, Math.min(g.left, Math.max(0, Math.min(fitSec, fitCbm, fitWt))));
          if (take > 0) {
            const share = Number.isFinite(capD) ? take / capD : 0;
            c.sections[i].contents.push({ length: g.length, butt: g.butt, qty: take, tag: g.tag, alloc: g.alloc, bund: g.bund, span: j - i + 1 });
            for (let k = i; k <= j; k++) c.sections[k].frac += share;
            c.lengths.add(g.length);
            c.diams.add(g.butt);
            c.cbm += take * g.cbmPP;
            c.wt += take * g.wtPP;
            g.left -= take;
          }
          break;
        }
      }
    }
    containers.push(c);
  }
  return { containers, stuck: false };
}

// Free typing, snap/clamp only on blur or Enter (snapping per keystroke made
// bundled lines impossible to type into).
function ManualCell({ value, onCommit, testid }) {
  const [draft, setDraft] = useState(value ? String(value) : "");
  useEffect(() => { setDraft(value ? String(value) : ""); }, [value]);
  return (
    <input type="number" min="0" className="w-16 px-1 py-0.5 text-right font-mono outline-none"
           value={draft}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={() => { const q = onCommit(draft); setDraft(q ? String(q) : ""); }}
           onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
           data-testid={testid} />
  );
}

export function ContainerEstimate({ lines, storageKey, defaultBooked, onPlan, testid = "container-estimate" }) {
  const key = storageKey || LS_KEY;
  const [rules, setRules] = useState(() => loadRules(key));
  const [open, setOpen] = useState(false);
  const setRule = (patch) => setRules((r) => {
    const next = { ...r, ...patch };
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const booked = rules.booked;
  const setBooked = (v) => setRule({ booked: v });

  const groups = useMemo(() => {
    const map = {};
    (lines || []).forEach((l) => {
      const b = Number(l.butt_size), L = Number(l.length);
      const q = Number(l.required_quantity ?? l.quantity) || 0;
      if (!b || !L || q <= 0) return;
      const tag = l.__tag || null, pri = Number(l.__pri) || 0;
      const alloc = l.__alloc === undefined ? null : !!l.__alloc;
      const bund = l.bundled && Number(l.poles_per_bundle) > 1 ? Math.floor(Number(l.poles_per_bundle)) : 1;
      const k = `${tag || ""}|${alloc}|${L}|${b}|${bund}`;
      map[k] = map[k] || { butt: b, length: L, qty: 0, tag, pri, alloc, bund, cbmPP: calcCbmPerPole(b, L) };
      map[k].qty += q;
    });
    const density = Number(rules.density) > 0 ? Number(rules.density) : 900;
    return Object.values(map).map((g) => ({ ...g, wtPP: g.cbmPP * density }));
  }, [lines, rules.density]);

  const result = useMemo(() => pack(groups, rules), [groups, rules]);

  const gkey = (g) => `${g.tag || ""}|${g.alloc}|${g.length}|${g.butt}|${g.bund}`;
  const [manual, setManual] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`containerManualPlan:${key}`) || "null") || { on: false, n: 0, alloc: {} }; }
    catch { return { on: false, n: 0, alloc: {} }; }
  });
  const saveManual = (m) => {
    setManual(m);
    try { localStorage.setItem(`containerManualPlan:${key}`, JSON.stringify(m)); } catch { /* ignore */ }
  };
  const [layoutEdit, setLayoutEdit] = useState(null); // container index being edited
  // Rule-derived numbers reused by the manual layout editor
  const der = useMemo(() => {
    const W = Number(rules.insideW) > 0 ? Number(rules.insideW) * 1000 : 0;
    const H = Number(rules.insideH) > 0 ? Number(rules.insideH) * 1000 : 0;
    const eff = Math.min(100, Number(rules.eff) > 0 ? Number(rules.eff) : 95);
    return {
      insideL: Number(rules.insideL) > 0 ? Number(rules.insideL) : Infinity,
      maxCbm: Number(rules.maxCbm) > 0 ? Number(rules.maxCbm) : Infinity,
      maxWt: Number(rules.maxWt) > 0 ? Number(rules.maxWt) : Infinity,
      cap: (d) => (W && H && d ? Math.max(1, Math.floor(Math.floor(W / d) * Math.floor(H / d) * eff / 100)) : Infinity),
    };
  }, [rules]);

  // Build a container straight from a user-defined layout (sections + placements).
  const buildFromLayout = (lo) => {
    const byKey = {};
    groups.forEach((g) => { byKey[gkey(g)] = g; });
    const sections = (lo.sections || []).map((s) => ({ length: Number(s.length) || 0, contents: [], frac: 0 }));
    let cbm = 0, wt = 0, qty = 0;
    const problems = [];
    (lo.sections || []).forEach((s, si) => {
      (s.items || []).forEach((it) => {
        const g = byKey[it.k];
        const q = Math.floor(Number(it.qty) || 0);
        if (!g || q <= 0) return;
        const capD = der.cap(g.butt);
        const share = Number.isFinite(capD) ? q / capD : 0;
        if (it.top || it.under) {
          let span = 0, j = si;
          while (j < sections.length && span < g.length - 1e-9) { span += sections[j].length; j += 1; }
          const tail = Math.max(0, der.insideL - sections.reduce((a, x) => a + x.length, 0));
          if (span + tail + 1e-9 < g.length) problems.push(`S${si + 1}: not enough run for ${g.length}m poles ${it.under ? "underneath" : "on top"}`);
          sections[si].contents.push({ length: g.length, butt: g.butt, qty: q, tag: g.tag, alloc: g.alloc, bund: g.bund, span: Math.max(1, j - si), under: !!it.under });
          for (let k2 = si; k2 < Math.min(Math.max(j, si + 1), sections.length); k2++) sections[k2].frac += share;
        } else {
          if (g.length > sections[si].length + 1e-9) problems.push(`S${si + 1}: ${g.length}m poles are longer than the ${sections[si].length}m section — place them "on top" / "underneath" or lengthen the section`);
          sections[si].contents.push({ length: g.length, butt: g.butt, qty: q, tag: g.tag, alloc: g.alloc, bund: g.bund });
          sections[si].frac += share;
        }
        cbm += q * g.cbmPP;
        wt += q * g.wtPP;
        qty += q;
      });
    });
    const floor = sections.reduce((a, s) => a + s.length, 0);
    sections.forEach((s, si) => {
      if (s.frac > 1 + 1e-6) problems.push(`S${si + 1} overfilled — ${Math.round(s.frac * 100)}% of the door opening`);
    });
    if (floor > der.insideL + 1e-6) problems.push(`floor ${floor.toFixed(1)}m exceeds inside length ${der.insideL}m`);
    if (cbm > der.maxCbm + 1e-6) problems.push(`over max m³ (${cbm.toFixed(1)})`);
    if (wt > der.maxWt + 1e-6) problems.push(`over max kg (${Math.round(wt).toLocaleString()})`);
    return { cbm, wt, floor, sections, fit: problems.length === 0, problems, qty, overQty: 0, layoutManual: true };
  };

  // Each manual container is validated with the real packing model (pack() on just
  // its assigned groups) — if it needs >1 container, the assignment doesn't fit.
  const manualResult = useMemo(() => {
    if (!manual.on) return null;
    const containers = [];
    for (let i = 0; i < manual.n; i++) {
      const lo = manual.layout?.[i];
      if (lo?.sections?.length) {
        containers.push(buildFromLayout(lo));
        continue;
      }
      const assigned = groups
        .map((g) => ({ ...g, qty: Math.min(g.qty, (manual.alloc[gkey(g)] || [])[i] || 0) }))
        .filter((g) => g.qty > 0);
      if (!assigned.length) {
        containers.push({ cbm: 0, wt: 0, floor: 0, sections: [], fit: true, overQty: 0, qty: 0 });
        continue;
      }
      const r = pack(assigned, rules);
      containers.push({
        cbm: r.containers.reduce((a, c) => a + c.cbm, 0),
        wt: r.containers.reduce((a, c) => a + c.wt, 0),
        floor: r.containers.reduce((a, c) => a + c.floor, 0),
        sections: r.containers.flatMap((c) => c.sections),
        fit: !r.stuck && r.containers.length <= 1,
        overQty: r.containers.slice(1).reduce((a, c) => a + c.sections.reduce((x, s) => x + s.contents.reduce((y, t) => y + t.qty, 0), 0), 0),
        qty: assigned.reduce((a, g) => a + g.qty, 0),
      });
    }
    return { containers, stuck: false };
  }, [manual, groups, rules]);

  const activePlan = manual.on
    ? { containers: (manualResult?.containers || []).filter((c) => c.qty > 0), stuck: false }
    : result;
  useEffect(() => { if (onPlan) onPlan(activePlan); });
  // Persist the rules server-side so exports (e.g. master workbook) reuse this setup.
  useEffect(() => {
    api.post("/stock/estimator-rules", { key, rules }).catch(() => {});
  }, [key, rules]);
  if (groups.length === 0) return null;

  const totCbm = groups.reduce((a, g) => a + g.qty * g.cbmPP, 0);
  const totWt = groups.reduce((a, g) => a + g.qty * g.wtPP, 0);
  const inp = "w-20 px-1.5 py-1 border border-stone-300 rounded text-xs font-mono";
  const needed = result.containers.length;
  const bookedEff = booked !== "" ? booked : (defaultBooked ?? "");
  const nBooked = Math.floor(Number(bookedEff)) > 0 ? Math.floor(Number(bookedEff)) : 0;
  const shown = nBooked > 0 ? result.containers.slice(0, nBooked) : result.containers;
  const overflow = nBooked > 0 && nBooked < needed
    ? result.containers.slice(nBooked).flatMap((c) => c.sections.flatMap((s) => s.contents))
    : [];
  const overflowQty = overflow.reduce((a, x) => a + x.qty, 0);

  const specTxt = (x) => `${x.tag ? `${x.tag} · ` : ""}${x.length}m Ø${x.butt} × ${x.qty}${x.bund > 1 ? ` (${Math.ceil(x.qty / x.bund)} bdl)` : ""}${x.span ? (x.under ? " (under)" : " (top)") : ""}${x.alloc === false ? " (plan)" : ""}`;
  const secText = (c) => c.sections
    .map((s) => s.contents.map(specTxt).join(" · "))
    .join("  |  ");

  const cellQty = (g, i) => {
    const lo = manual.layout?.[i];
    if (lo?.sections?.length) {
      let t = 0;
      lo.sections.forEach((s) => (s.items || []).forEach((it) => { if (it.k === gkey(g)) t += Math.floor(Number(it.qty) || 0); }));
      return t;
    }
    return Number((manual.alloc[gkey(g)] || [])[i]) || 0;
  };
  const assignedRow = (g) => Array.from({ length: manual.n }, (_, i) => cellQty(g, i)).reduce((a, b) => a + b, 0);
  const setCell = (g, i, v) => {
    const k = gkey(g);
    const row = (manual.alloc[k] || []).slice();
    const others = assignedRow(g) - cellQty(g, i);
    const maxCell = Math.max(0, g.qty - others);
    let q = Math.max(0, Math.min(Math.floor(Number(v) || 0), maxCell));
    if (g.bund > 1 && q < maxCell) q = Math.floor(q / g.bund) * g.bund;
    row[i] = q;
    saveManual({ ...manual, alloc: { ...manual.alloc, [k]: row } });
    return q;
  };
  const startManual = (prefill) => {
    const alloc = {};
    let n = Math.max(1, nBooked || result.containers.length || 1);
    if (prefill) {
      n = Math.max(1, result.containers.length);
      result.containers.forEach((c, i) => c.sections.forEach((s) => s.contents.forEach((x) => {
        const k = `${x.tag || ""}|${x.alloc}|${x.length}|${x.butt}|${x.bund || 1}`;
        alloc[k] = alloc[k] || [];
        alloc[k][i] = (alloc[k][i] || 0) + x.qty;
      })));
    }
    saveManual({ on: true, n, alloc });
  };
  const unassigned = manual.on ? groups.filter((g) => g.qty - assignedRow(g) > 0) : [];

  const setLayout = (i, lo) => saveManual({ ...manual, layout: { ...(manual.layout || {}), [i]: lo } });
  const clearLayout = (i) => {
    const layout = { ...(manual.layout || {}) };
    delete layout[i];
    saveManual({ ...manual, layout });
  };
  const seedLayout = (i) => {
    const c = manualResult?.containers?.[i];
    const sections = (c?.sections || []).map((s) => ({
      length: s.length,
      items: s.contents.map((x) => ({ k: `${x.tag || ""}|${x.alloc}|${x.length}|${x.butt}|${x.bund}`, qty: x.qty, top: !!x.span && !x.under, under: !!x.under })),
    }));
    setLayout(i, { sections: sections.length ? sections : [{ length: "", items: [] }] });
    setLayoutEdit(i);
  };
  const groupLabel = (g) => `${g.tag ? `${g.tag} · ` : ""}${g.length}m Ø${g.butt}${g.bund > 1 ? ` (${g.bund}/bdl)` : ""}${g.alloc === false ? " (plan)" : ""}`;

  return (
    <div className="panel p-3 bg-sky-50/40 border-sky-200" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 font-semibold text-sm text-sky-900" data-testid={`${testid}-toggle`}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Container className="h-4 w-4" />
          Estimated containers: <span className="font-mono text-base">{manual.on ? manual.n : (result.stuck ? "?" : needed)}</span>
          {manual.on && <span className="text-[10px] font-bold uppercase bg-amber-200 text-amber-900 rounded px-1 py-0.5">manual</span>}
        </button>
        <div className="text-xs text-stone-500">{totCbm.toFixed(1)} m³ · {Math.round(totWt).toLocaleString()} kg total (est.)</div>
        {nBooked > 0 && !result.stuck && (
          nBooked >= needed ? (
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`${testid}-booked-ok`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Fits in {nBooked} booked{nBooked > needed ? ` (${nBooked - needed} spare)` : ""}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-semibold text-red-600" data-testid={`${testid}-booked-short`}>
              <AlertTriangle className="h-3.5 w-3.5" /> Short by {needed - nBooked} container{needed - nBooked > 1 ? "s" : ""} ({overflowQty} poles won't fit)
            </span>
          )
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3 text-xs">
            <label className="flex flex-col gap-0.5">Inside length m
              <input className={inp} type="number" value={rules.insideL} onChange={(e) => setRule({ insideL: e.target.value })} data-testid={`${testid}-inside-l`} /></label>
            <label className="flex flex-col gap-0.5">Inside width m
              <input className={inp} type="number" value={rules.insideW} onChange={(e) => setRule({ insideW: e.target.value })} data-testid={`${testid}-inside-w`} /></label>
            <label className="flex flex-col gap-0.5">Inside height m
              <input className={inp} type="number" value={rules.insideH} onChange={(e) => setRule({ insideH: e.target.value })} data-testid={`${testid}-inside-h`} /></label>
            <label className="flex flex-col gap-0.5">Stacking eff. %
              <input className={inp} type="number" value={rules.eff} onChange={(e) => setRule({ eff: e.target.value })} data-testid={`${testid}-eff`} /></label>
            <label className="flex flex-col gap-0.5">Max m³ / cont.
              <input className={inp} type="number" value={rules.maxCbm} onChange={(e) => setRule({ maxCbm: e.target.value })} data-testid={`${testid}-max-cbm`} /></label>
            <label className="flex flex-col gap-0.5">Max kg / cont.
              <input className={inp} type="number" value={rules.maxWt} onChange={(e) => setRule({ maxWt: e.target.value })} data-testid={`${testid}-max-wt`} /></label>
            <label className="flex flex-col gap-0.5">Density kg/m³
              <input className={inp} type="number" value={rules.density} onChange={(e) => setRule({ density: e.target.value })} data-testid={`${testid}-density`} /></label>
            <label className="flex flex-col gap-0.5">Max lengths / cont.
              <input className={inp} type="number" placeholder="any" value={rules.maxLengths} onChange={(e) => setRule({ maxLengths: e.target.value })} data-testid={`${testid}-max-lengths`} /></label>
            <label className="flex flex-col gap-0.5">Max diameters / cont.
              <input className={inp} type="number" placeholder="any" value={rules.maxDiams} onChange={(e) => setRule({ maxDiams: e.target.value })} data-testid={`${testid}-max-diams`} /></label>
            <label className="flex flex-col gap-0.5 text-sky-900 font-semibold">Booked cont.
              <input className={inp + " border-sky-400"} type="number" placeholder="auto" value={booked} onChange={(e) => setBooked(e.target.value)} data-testid={`${testid}-booked`} /></label>
            <label className="flex items-center gap-1.5 pb-1.5 font-semibold text-sky-900 cursor-pointer" title="Ignore the order picking priority and mix orders freely for the tightest possible packing">
              <input type="checkbox" checked={!!rules.bestFit} onChange={(e) => setRule({ bestFit: e.target.checked ? "1" : "" })} data-testid={`${testid}-best-fit`} />
              Best fit (ignore priority)
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-sky-900">Plan mode:</span>
            <button onClick={() => saveManual({ ...manual, on: false })}
                    className={`px-2 py-1 rounded border ${!manual.on ? "bg-sky-700 text-white border-sky-700" : "bg-white border-stone-300"}`}
                    data-testid={`${testid}-mode-auto`}>Auto estimate</button>
            <button onClick={() => (manual.n > 0 ? saveManual({ ...manual, on: true }) : startManual(false))}
                    className={`px-2 py-1 rounded border ${manual.on ? "bg-amber-600 text-white border-amber-600" : "bg-white border-stone-300"}`}
                    data-testid={`${testid}-mode-manual`}>Manual plan</button>
            {manual.on && (
              <>
                <button onClick={() => startManual(true)} className="px-2 py-1 rounded border border-stone-300 bg-white" data-testid={`${testid}-manual-prefill`}>
                  Prefill from auto estimate
                </button>
                <button onClick={() => saveManual({ ...manual, n: manual.n + 1 })} className="px-2 py-1 rounded border border-stone-300 bg-white" data-testid={`${testid}-manual-add`}>
                  + container
                </button>
                <button onClick={() => manual.n > 1 && saveManual({ ...manual, n: manual.n - 1 })} className="px-2 py-1 rounded border border-stone-300 bg-white" data-testid={`${testid}-manual-remove`}>
                  − container
                </button>
                <span className="text-stone-500">The picking slip, layout drawings and Mark Ready follow this manual plan.</span>
              </>
            )}
          </div>
          {manual.on ? (
            <div className="space-y-2">
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse" data-testid={`${testid}-manual-grid`}>
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-1 bg-sky-100 border border-sky-200 sticky left-0">Spec (qty to ship)</th>
                      {Array.from({ length: manual.n }, (_, i) => (
                        <th key={i} className="px-2 py-1 bg-sky-100 border border-sky-200 font-mono">#{i + 1}</th>
                      ))}
                      <th className="px-2 py-1 bg-sky-100 border border-sky-200">Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, ri) => {
                      const left = g.qty - assignedRow(g);
                      return (
                        <tr key={gkey(g)}>
                          <td className="px-2 py-1 border border-stone-200 bg-white whitespace-nowrap sticky left-0">
                            {g.tag ? `${g.tag} · ` : ""}{g.length}m Ø{g.butt}{g.bund > 1 ? ` (${g.bund}/bdl)` : ""}{g.alloc === false ? " (plan)" : ""}
                            <span className="text-stone-400"> — {g.qty.toLocaleString()}</span>
                          </td>
                          {Array.from({ length: manual.n }, (_, i) => (
                            <td key={i} className="border border-stone-200 bg-white">
                              {manual.layout?.[i]?.sections?.length ? (
                                <div className="w-16 px-1 py-0.5 text-right font-mono text-stone-400" title="This container uses a custom layout — edit quantities there">
                                  {cellQty(g, i) || ""}
                                </div>
                              ) : (
                                <ManualCell value={cellQty(g, i)}
                                            onCommit={(v) => setCell(g, i, v)}
                                            testid={`${testid}-manual-cell-${ri}-${i}`} />
                              )}
                            </td>
                          ))}
                          <td className={`px-2 py-1 border border-stone-200 text-right font-mono ${left > 0 ? "bg-amber-50 text-amber-800 font-bold" : "bg-emerald-50 text-emerald-700"}`}>
                            {left.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="px-2 py-1 border border-stone-200 bg-sky-50 font-semibold sticky left-0">Fit check</td>
                      {(manualResult?.containers || []).map((c, i) => (
                        <td key={i} className="px-1 py-1 border border-stone-200 bg-sky-50 text-center" data-testid={`${testid}-manual-fit-${i}`}>
                          {c.qty === 0 ? (
                            <span className="text-stone-400">empty</span>
                          ) : c.fit ? (
                            <span className="text-emerald-700 font-bold">✓ fits</span>
                          ) : (
                            <span className="text-red-600 font-bold">✗ {c.layoutManual ? "layout issue" : `over by ${c.overQty.toLocaleString()}`}</span>
                          )}
                          <div className="text-[10px] text-stone-500 font-mono">
                            {c.floor.toFixed(1)}m · {c.cbm.toFixed(1)}m³
                            <br />{Math.round(c.wt).toLocaleString()}kg
                          </div>
                        </td>
                      ))}
                      <td className="border border-stone-200 bg-sky-50" />
                    </tr>
                  </tbody>
                </table>
              </div>
              {unassigned.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid={`${testid}-manual-unassigned`}>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  <span className="font-semibold text-amber-800">Not assigned to any container:</span>
                  <span className="flex-1 text-amber-800">
                    {unassigned.map((g) => `${g.tag ? `${g.tag} · ` : ""}${g.length}m Ø${g.butt} × ${(g.qty - assignedRow(g)).toLocaleString()}`).join("  ·  ")}
                  </span>
                </div>
              )}
              <div className="space-y-1">
                {(manualResult?.containers || []).map((c, i) => (c.qty > 0 || manual.layout?.[i]?.sections?.length) && (
                  <div key={i} className="bg-white border border-stone-200 rounded" data-testid={`${testid}-manual-cont-${i}`}>
                    <div className="flex flex-wrap items-center gap-2 text-xs px-2 py-1.5">
                      <span className="font-mono font-bold text-sky-900">#{i + 1}</span>
                      {c.layoutManual && <span className="text-[10px] font-bold uppercase bg-violet-200 text-violet-900 rounded px-1">custom layout</span>}
                      {!c.fit && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded px-1">doesn't fit</span>}
                      <span className="flex-1">{secText(c)}</span>
                      <span className="font-mono text-stone-500">{c.floor.toFixed(1)}m floor · {c.cbm.toFixed(1)} m³ · {Math.round(c.wt).toLocaleString()} kg</span>
                      <button className="px-1.5 py-0.5 rounded border border-stone-300"
                              onClick={() => (manual.layout?.[i]?.sections?.length ? setLayoutEdit(layoutEdit === i ? null : i) : seedLayout(i))}
                              data-testid={`${testid}-manual-layout-btn-${i}`}>
                        {manual.layout?.[i]?.sections?.length ? (layoutEdit === i ? "Close editor" : "Edit layout") : "Edit layout"}
                      </button>
                      {manual.layout?.[i]?.sections?.length > 0 && (
                        <button className="px-1.5 py-0.5 rounded border border-stone-300 text-stone-500"
                                onClick={() => { clearLayout(i); if (layoutEdit === i) setLayoutEdit(null); }}
                                data-testid={`${testid}-manual-layout-reset-${i}`}>Reset to auto</button>
                      )}
                    </div>
                    {layoutEdit === i && manual.layout?.[i]?.sections?.length > 0 && (
                      <div className="border-t border-stone-200 bg-violet-50/40 px-2 py-2 space-y-2 text-xs" data-testid={`${testid}-layout-editor-${i}`}>
                        {(c.problems || []).length > 0 ? (
                          <div className="text-red-700 font-semibold">
                            {(c.problems || []).map((p, pi) => <div key={pi}>✗ {p}</div>)}
                          </div>
                        ) : (
                          <div className="text-emerald-700 font-semibold">✓ this layout fits</div>
                        )}
                        {manual.layout[i].sections.map((s, si) => (
                          <div key={si} className="bg-white border border-stone-200 rounded px-2 py-1.5 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sky-900">S{si + 1}</span>
                              <span>section length</span>
                              <input type="number" step="0.1" min="0" className="w-16 px-1 py-0.5 border border-stone-300 rounded text-right font-mono"
                                     value={s.length}
                                     onChange={(e) => {
                                       const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                       lo.sections[si].length = e.target.value;
                                       setLayout(i, lo);
                                     }}
                                     data-testid={`${testid}-layout-seclen-${i}-${si}`} />
                              <span>m</span>
                              <span className="font-mono text-stone-500">
                                {Math.round(((c.sections[si]?.frac || 0)) * 100)}% of door opening
                              </span>
                              <button className="ml-auto text-red-500" title="Remove section"
                                      onClick={() => {
                                        const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                        lo.sections.splice(si, 1);
                                        setLayout(i, lo.sections.length ? lo : { sections: [{ length: "", items: [] }] });
                                      }}>✕</button>
                            </div>
                            {(s.items || []).map((it, ii) => (
                              <div key={ii} className="flex items-center gap-2 pl-5">
                                <select className="border border-stone-300 rounded px-1 py-0.5 max-w-[280px]"
                                        value={it.k}
                                        onChange={(e) => {
                                          const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                          lo.sections[si].items[ii].k = e.target.value;
                                          setLayout(i, lo);
                                        }}
                                        data-testid={`${testid}-layout-item-spec-${i}-${si}-${ii}`}>
                                  <option value="">— pick spec —</option>
                                  {groups.map((g) => <option key={gkey(g)} value={gkey(g)}>{groupLabel(g)} — {g.qty.toLocaleString()}</option>)}
                                </select>
                                <input type="number" min="0" className="w-20 px-1 py-0.5 border border-stone-300 rounded text-right font-mono"
                                       value={it.qty}
                                       onChange={(e) => {
                                         const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                         lo.sections[si].items[ii].qty = e.target.value;
                                         setLayout(i, lo);
                                       }}
                                       data-testid={`${testid}-layout-item-qty-${i}-${si}-${ii}`} />
                                <select className="border border-stone-300 rounded px-1 py-0.5"
                                        value={it.under ? "under" : it.top ? "top" : "in"}
                                        title='"On top" / "underneath" poles span from this section across the following ones'
                                        onChange={(e) => {
                                          const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                          lo.sections[si].items[ii].top = e.target.value === "top";
                                          lo.sections[si].items[ii].under = e.target.value === "under";
                                          setLayout(i, lo);
                                        }}
                                        data-testid={`${testid}-layout-item-place-${i}-${si}-${ii}`}>
                                  <option value="in">in section</option>
                                  <option value="top">on top</option>
                                  <option value="under">underneath</option>
                                </select>
                                <button className="text-red-500" title="Remove line"
                                        onClick={() => {
                                          const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                          lo.sections[si].items.splice(ii, 1);
                                          setLayout(i, lo);
                                        }}>✕</button>
                              </div>
                            ))}
                            <button className="pl-5 text-sky-700 font-semibold"
                                    onClick={() => {
                                      const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                      lo.sections[si].items = lo.sections[si].items || [];
                                      lo.sections[si].items.push({ k: "", qty: "", top: false });
                                      setLayout(i, lo);
                                    }}
                                    data-testid={`${testid}-layout-add-item-${i}-${si}`}>+ pole line</button>
                          </div>
                        ))}
                        <button className="text-sky-700 font-semibold"
                                onClick={() => {
                                  const lo = JSON.parse(JSON.stringify(manual.layout[i]));
                                  lo.sections.push({ length: "", items: [] });
                                  setLayout(i, lo);
                                }}
                                data-testid={`${testid}-layout-add-section-${i}`}>+ section</button>
                        <div className="text-stone-500">
                          Moving stock here overrides the grid quantities for this container. "On top" poles hang from the roof and "underneath" poles lie on the floor, both spanning from their section across the following sections (and any free floor); the section stock stacks between them. Picking slips and layout drawings follow this exact arrangement.
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : result.stuck ? (
            <div className="text-xs text-red-600">Cannot fit a single pole with these limits — check dimensions and max m³ / kg.</div>
          ) : (
            <div className="space-y-1">
              {shown.map((c, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-xs bg-white border border-stone-200 rounded px-2 py-1.5" data-testid={`${testid}-cont-${i}`}>
                  <span className="font-mono font-bold text-sky-900">#{i + 1}</span>
                  <span className="flex-1">{secText(c)}</span>
                  <span className="font-mono text-stone-500">{c.floor.toFixed(1)}m floor · {c.cbm.toFixed(1)} m³ · {Math.round(c.wt).toLocaleString()} kg</span>
                </div>
              ))}
              {overflow.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs bg-red-50 border border-red-300 rounded px-2 py-1.5" data-testid={`${testid}-overflow`}>
                  <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                  <span className="font-semibold text-red-700">Won't fit in {nBooked} booked:</span>
                  <span className="flex-1 text-red-700">{overflow.map(specTxt).join("  ·  ")}</span>
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] text-stone-500">
            Estimate only — poles are packed in length sections along the container floor (e.g. 7m + 5m = 12m), so two 7m rows never share one container. Shorter poles top up spare space inside a longer section (e.g. 4m poles slot in with 5m poles), and long leftover poles may ride on top spanning adjacent sections (marked "(top)"). Bundled lines are never split — whole bundles move together between containers. Section capacity comes from inside width × height ÷ pole diameter × stacking efficiency. Leave a rule blank for no restriction; each order keeps its own rules.
          </div>
        </div>
      )}
    </div>
  );
}
